import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { readFragment } from "gql.tada";
import { useStore } from "@/app/store";
import { UPDATE_FORMULA } from "../graphql/mutations";
import { CALCULATION_DETAIL_FRAGMENT } from "../graphql/queries";
import { mapBackendError } from "../formula/mapBackendError";
import type { FormulaValidationError } from "../formula/validateFormula";
import useOpDataNotPopulatedHandler from "./useOpDataNotPopulatedHandler";
import useStaleVersionHandler from "./useStaleVersionHandler";

export interface UpdateFormulaArgs {
  calculationId: string;
  subCategoryId: string;
  expression: string;
  dimensionValue: string | null;
}

export type UpdateFormulaOutcome =
  | { kind: "saved" }
  | { kind: "conflict" } // stale version; onConflict already fired
  | { kind: "opDataNotPopulated" } // op-data handler already toasted
  | { kind: "failed" } // calc missing / network / unmapped error
  | { kind: "rejected"; error: FormulaValidationError }; // backend validation rejection

const useUpdateFormula = (onConflict: () => void) => {
  const handleStale = useStaleVersionHandler(onConflict);
  const handleOpDataNotPopulated = useOpDataNotPopulatedHandler();
  const setCalculation = useStore((s) => s.setCalculation);
  const [runMutation, { loading }] = useMutation(UPDATE_FORMULA);

  const updateFormula = useCallback(
    async (args: UpdateFormulaArgs): Promise<UpdateFormulaOutcome> => {
      const { calculation } = useStore.getState();
      if (!calculation) return { kind: "failed" };

      const calledForId = calculation.id;
      const result = await runMutation({
        variables: {
          input: { ...args, expectedVersion: calculation.version },
        },
      });

      if (result.error) {
        if (handleOpDataNotPopulated(result.error))
          return { kind: "opDataNotPopulated" };
        if (handleStale(result.error)) return { kind: "conflict" };

        const mapped = mapBackendError(result.error);
        if (mapped) return { kind: "rejected", error: mapped };

        console.error("updateFormula failed:", result.error);
        return { kind: "failed" };
      }

      if (result.data?.updateFormula) {
        setCalculation(
          readFragment(CALCULATION_DETAIL_FRAGMENT, result.data.updateFormula),
          calledForId,
        );
      }
      return { kind: "saved" };
    },
    [runMutation, handleStale, handleOpDataNotPopulated, setCalculation],
  );

  return { updateFormula, isSaving: loading };
};

export default useUpdateFormula;
